import PencomTextField from "@/components/input/PencomTextField";
import type { ComponentType, ReactElement } from "react";

type EmployerDetailsField = {
  name: string;
  state: {
    value: string;
    meta: {
      errors: string[];
    };
  };
  handleChange: (value: string) => void;
};

type EmployerDetailsFieldValidators = {
  onChange: (args: { value: string }) => string | undefined;
};

type IdentityFieldName =
  | "companyName"
  | "organizationNumber"
  | "organizationTypeCode"
  | "sectorCode"
  | "employerAddress";

type EmployerDetailsFormLike = {
  Field: ComponentType<{
    name: IdentityFieldName;
    validators?: EmployerDetailsFieldValidators | undefined;
    children: (field: unknown) => ReactElement;
  }>;
};

export interface EmployerDetailsIdentityFieldsSectionProps {
  form: EmployerDetailsFormLike;
  lookupData: { sectorName?: string } | null | undefined;
  selectedOrgType: { label: string } | null | undefined;
  organizationNumberLabel: string;
  organizationNumberPrefix: string;
}

export const EmployerDetailsIdentityFieldsSection = ({
  form,
  lookupData,
  selectedOrgType,
  organizationNumberLabel,
  organizationNumberPrefix,
}: EmployerDetailsIdentityFieldsSectionProps) => {
  return (
    <>
      <div className="flex flex-row gap-x-11">
        <div className="flex-1">
          <form.Field
            name="companyName"
            validators={{
              onChange: ({ value }: { value: string }) => {
                if (!value) return "Organization Name is required";
                if (value.length < 2)
                  return "Name must be at least 2 characters";
                return undefined;
              },
            }}
            children={(field: unknown) => {
              const f = field as EmployerDetailsField;
              return (
                <PencomTextField
                  id={f.name}
                  label="Organization Name"
                  placeholder="Enter organization name"
                  type="text"
                  value={f.state.value}
                  onChange={(e) => f.handleChange(e.target.value)}
                  disabled={true}
                  bg="#F6F7F9"
                  error={!!f.state.meta.errors.length}
                  helperText={f.state.meta.errors.join(", ")}
                />
              );
            }}
          />
        </div>
        <div className="flex-1">
          <form.Field
            name="organizationNumber"
            validators={{
              onChange: ({ value }: { value: string }) => {
                if (!value) return `${organizationNumberLabel} is required`;
                if (!/^\d+$/.test(value)) return "Must be numeric";
                return undefined;
              },
            }}
            children={(field: unknown) => {
              const f = field as EmployerDetailsField;
              return (
                <div className="flex flex-col gap-y-2">
                  <label className="font-clash-display text-medium-dark-grey text-md font-semibold">
                    {organizationNumberLabel}
                  </label>
                  {organizationNumberLabel === "RC Number" && (
                    <span className="text-medium-grey text-xs font-regular font-clash-display">
                      Kindly enter the RC Number that&apos;s on your CAC
                      certificate.
                    </span>
                  )}
                  <div className="flex items-center gap-x-2">
                    {organizationNumberPrefix && (
                      <div className="px-4 py-3 bg-gray-100 border border-stroke rounded-lg text-medium-dark-grey text-xs font-clash-display min-w-[60px] text-center">
                        {organizationNumberPrefix}
                      </div>
                    )}
                    <div className="flex-1">
                      <PencomTextField
                        id={f.name}
                        label=""
                        placeholder={`Enter ${organizationNumberLabel.toLowerCase()}`}
                        type="text"
                        value={f.state.value}
                        onChange={(e) => f.handleChange(e.target.value)}
                        bg="white"
                        error={!!f.state.meta.errors.length}
                        helperText={f.state.meta.errors.join(", ")}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </div>
      </div>

      <div className="flex flex-row gap-x-11">
        <div className="flex-1">
          <form.Field
            name="organizationTypeCode"
            children={(field: unknown) => {
              const f = field as EmployerDetailsField;
              return (
                <PencomTextField
                  id={f.name}
                  label="Organization Type"
                  placeholder="Organization type"
                  type="text"
                  value={selectedOrgType?.label || f.state.value}
                  onChange={() => {}}
                  disabled={true}
                  bg="#F6F7F9"
                />
              );
            }}
          />
        </div>
        <div className="flex-1">
          <form.Field
            name="sectorCode"
            children={(field: unknown) => {
              const f = field as EmployerDetailsField;
              return (
                <PencomTextField
                  id={f.name}
                  label="Sector"
                  placeholder="Sector"
                  type="text"
                  value={lookupData?.sectorName || f.state.value}
                  onChange={() => {}}
                  disabled={true}
                  bg="#F6F7F9"
                />
              );
            }}
          />
        </div>
      </div>

      <form.Field
        name="employerAddress"
        validators={{
          onChange: ({ value }: { value: string }) => {
            if (!value) return "Employer Address is required";
            return undefined;
          },
        }}
        children={(field: unknown) => {
          const f = field as EmployerDetailsField;
          return (
            <PencomTextField
              id={f.name}
              label="Organization Address"
              placeholder="Enter organization address"
              type="text"
              value={f.state.value}
              onChange={(e) => f.handleChange(e.target.value)}
              bg="white"
              multiline
              rows={4}
              onTextareaChange={(e) => f.handleChange(e.target.value)}
              error={!!f.state.meta.errors.length}
              helperText={f.state.meta.errors.join(", ")}
            />
          );
        }}
      />
    </>
  );
};
